import {
  Carrier,
  getTrackingLink,
  Service,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { RedoShopifyDeliveryMethodType } from "@redotech/redo-model/fulfillments/fulfillment-delivery-method-type";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { OMSEventType } from "@redotech/redo-model/fulfillments/fulfillment-group-events";
import { FulfillmentGroupStatus } from "@redotech/redo-model/fulfillments/fulfillment-group-status";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import { RateLandedCost } from "@redotech/redo-model/landed-cost/rate-landed-cost";
import { Provider } from "@redotech/redo-model/order";
import { FulfillmentGroupActions } from "@redotech/redo-model/outbound-labels/fulfillment-group-actions";
import {
  isThirdPartyOnlyPayingVat,
  OutboundRate,
  RateProvider,
  ShipmentPaymentOptionType,
} from "@redotech/redo-model/outbound-labels/outbound-labels";
import {
  isParcelEqual,
  Parcel,
} from "@redotech/redo-model/outbound-labels/parcel";
import { Preset } from "@redotech/redo-model/outbound-labels/presets";
import { Weight } from "@redotech/redo-model/outbound-labels/util";
import { arrayEqual } from "@redotech/util/equal";
export interface OutboundRateOption {
  key?: string;
  carrier: Carrier;
  service: Service;
  rate?: number;
  currency?: string;
  deliveryDays?: string;
  transitDays?: string;
  redoRate?: boolean;
  carrier_account_id?: string;
  saturdayDelivery?: boolean;
  provider?: RateProvider;
  landedCost?: RateLandedCost;
  isThirdPartyBilling?: boolean;
  thirdPartyBillingAccountId?: string;
}

const ProviderCannotUpdateTrackingInfo: Provider[] = [
  Provider.WALMART,
  Provider.AMAZON,
  Provider.EBAY,
];

export function getNoLabelErrorMessage(
  fulfillmentGroups: readonly FulfillmentGroupData[],
): string {
  if (fulfillmentGroups.length === 0) {
    return "No label to print — label unavailable for this order. Check order source for details";
  }

  const conditions: string[] = [];

  for (const group of fulfillmentGroups) {
    if (
      group.status === FulfillmentGroupStatus.Cancelled &&
      !conditions.includes("canceled")
    ) {
      conditions.push("canceled");
    }

    if (group.isRefunded && !conditions.includes("refunded")) {
      conditions.push("refunded");
    }

    if (
      group.events?.some(
        (event) => event.type === OMSEventType.MARKED_AS_FULFILLED,
      ) &&
      !conditions.includes("manually marked as fulfilled")
    ) {
      conditions.push("manually marked as fulfilled");
    }
  }

  if (conditions.length > 0) {
    const conditionText =
      conditions.length === 1
        ? conditions[0]
        : `${conditions.slice(0, -1).join(", ")} and ${conditions[conditions.length - 1]}`;

    const orderText =
      fulfillmentGroups.length === 1 ? "order was" : "orders were";
    return `No label${fulfillmentGroups.length === 1 ? "" : "s"} to print — ${orderText} ${conditionText}.`;
  }

  return fulfillmentGroups.length === 1
    ? "No label to print — no label was purchased within redo"
    : "No labels to print — no labels were purchased within redo";
}

export function summarizeOutboundRateOptions(
  fulfillmentGroups: readonly FulfillmentGroupData[],
): readonly OutboundRateOption[] {
  if (fulfillmentGroups.length === 0) {
    return [];
  }
  if (fulfillmentGroups.length === 1) {
    return (
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      fulfillmentGroups[0].availableShipmentRates?.rates.map((rate) => ({
        key: `${rate.carrier}-${rate.service}-${!rate.is_merchant_carrier_account}-${rate.carrier_account_id}`,
        carrier: rate.carrier,
        service: rate.service,
        rate: Number(rate.rate),
        currency: rate.currency,
        deliveryDays: rate.delivery_days?.toString(),
        transitDays: rate.transit_days?.toString(),
        redoRate: !rate.is_merchant_carrier_account,
        carrier_account_id: rate.carrier_account_id,
        saturdayDelivery: rate.saturday_delivery ?? undefined,
        provider: rate.provider,
        landedCost: rate.landedCost ?? undefined,
        isThirdPartyBilling:
          rate.payment_option?.type === ShipmentPaymentOptionType.THIRD_PARTY,
        thirdPartyBillingAccountId:
          rate.payment_option?.type === ShipmentPaymentOptionType.THIRD_PARTY
            ? (rate.payment_option.thirdPartyBillingAccountId ?? undefined)
            : undefined,
      })) || []
    );
  }

  // For multiple fulfillment groups, we need to group by carrier, service, and redoRate
  const groupRateMaps = fulfillmentGroups.map((group) => {
    const rateMap = new Map<string, OutboundRate>();
    for (const rate of group.availableShipmentRates?.rates || []) {
      // Group by carrier, service, and whether it's redo provided
      const isRedoRate = !rate.is_merchant_carrier_account;
      const key = `${rate.carrier}-${rate.service}-${isRedoRate}`;

      const existingRate = rateMap.get(key);
      if (!existingRate || Number(existingRate.rate) > Number(rate.rate)) {
        rateMap.set(key, rate);
      }
    }
    return rateMap;
  });

  // Find common carrier/service/redoRate combinations across all groups
  const commonOptions = new Set<string>();

  if (groupRateMaps.length > 0 && groupRateMaps[0]) {
    for (const key of groupRateMaps[0].keys()) {
      if (groupRateMaps.every((map) => map.has(key))) {
        commonOptions.add(key);
      }
    }
  }

  // Generate the final rate options
  const rates: OutboundRateOption[] = [];

  for (const key of commonOptions) {
    // Parse the key to get carrier, service, and redoRate
    const redoRateStr = key.split("-")[2];
    const redoRate = redoRateStr === "true";

    const groupRates = groupRateMaps.map((map) => map.get(key)!);

    if (groupRates.length > 0) {
      const firstRate = groupRates[0];

      // Calculate total rate price (sum of all group rates)
      const totalRate = groupRates.reduce(
        (sum, rate) => sum + Number(rate.rate),
        0,
      );

      rates.push({
        key: `${key}-multiple`,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        carrier: firstRate.carrier,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        service: firstRate.service,
        rate: totalRate,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        currency: firstRate.currency,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        deliveryDays: firstRate.delivery_days?.toString(),
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        transitDays: firstRate.transit_days?.toString(),
        redoRate,
        carrier_account_id: undefined, // Multiple groups may have different account IDs
        isThirdPartyBilling: groupRates.every(
          (rate) =>
            rate?.payment_option?.type ===
            ShipmentPaymentOptionType.THIRD_PARTY,
        ),
        thirdPartyBillingAccountId: (() => {
          const ids = groupRates.map((rate) =>
            rate?.payment_option?.type === ShipmentPaymentOptionType.THIRD_PARTY
              ? rate.payment_option.thirdPartyBillingAccountId
              : undefined,
          );
          const first = ids[0];
          return first && ids.every((id) => id === first) ? first : undefined;
        })(),
      });
    }
  }

  return rates;
}

/*
 * An error message is meant to be shown under the button and prevent the user from taking the action
 * A warning message is meant to be shown under the button while allowing the user to take the action
 * A void confirmation message is meant to be shown in a modal before the user can void the label
 */
export function actionMessages({
  currentValue,
  fulfillmentGroups,
  selectedParcels,
  mixedParcels,
  totalRatePrice,
  needsFunds,
  balance,
  balanceLoading,
  selectedPreset,
  selectedRate,
  deliveryMethod,
  totalCustomsDeclarationWeight,
  totalWeight,
  destinationAddress,
  originAddress,
  ratesPending,
}: {
  currentValue: FulfillmentGroupActions;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  selectedParcels: Parcel[] | null;
  mixedParcels: boolean;
  totalRatePrice: number | null;
  needsFunds: boolean;
  balance: string | undefined;
  balanceLoading: boolean;
  selectedPreset: Preset | undefined;
  selectedRate: OutboundRateOption | null;
  deliveryMethod: RedoShopifyDeliveryMethodType | null | undefined;
  totalCustomsDeclarationWeight: Weight | null | undefined;
  totalWeight: Weight | null | undefined;
  destinationAddress: FulfillmentOrderAddress | undefined | null;
  originAddress: FulfillmentOrderAddress | undefined | null;
  ratesPending: boolean;
}): {
  errorMessage: string;
  warningMessage: string;
  voidConfirmationMessage: string;
} {
  const noParcel =
    (!selectedParcels || selectedParcels.length === 0) && !mixedParcels;
  const noRate = totalRatePrice == null;

  const isSingleUnprintableSelection =
    fulfillmentGroups.length === 1 &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    !fulfillmentGroups[0].shipment?.label?.url &&
    currentValue === FulfillmentGroupActions.PRINT_LABEL &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    fulfillmentGroups[0].deliveryMethod ===
      RedoShopifyDeliveryMethodType.Shipping;

  const allSelectionUnprintable =
    fulfillmentGroups.length > 0 &&
    fulfillmentGroups.every((fg) => !fg.shipment?.label?.url) &&
    currentValue === FulfillmentGroupActions.PRINT_LABEL &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    fulfillmentGroups[0].deliveryMethod ===
      RedoShopifyDeliveryMethodType.Shipping;

  const hasUnprintableSelection =
    fulfillmentGroups.some((fg) => !fg.shipment?.label?.url) &&
    currentValue === FulfillmentGroupActions.PRINT_LABEL &&
    fulfillmentGroups.some(
      (fg) => fg.deliveryMethod === RedoShopifyDeliveryMethodType.Shipping,
    );

  const ordersOnHold = fulfillmentGroups.filter(
    (fg) => fg.status === FulfillmentGroupStatus.OnHold,
  );

  const isCreateAction =
    currentValue === FulfillmentGroupActions.CREATE_LABEL ||
    currentValue === FulfillmentGroupActions.CREATE_AND_PRINT_LABEL;

  let errorMessage = "";
  let warningMessage = "";
  let voidConfirmationMessage = "";
  if (isCreateAction) {
    if (
      noRate &&
      deliveryMethod === RedoShopifyDeliveryMethodType.Shipping &&
      !ratesPending
    ) {
      errorMessage = "No rate selected";
    }
    if (noParcel) {
      errorMessage = "No parcel selected";
    }
    if (needsFunds) {
      const msg = "Insufficient funds to purchase labels.";
      errorMessage = msg;
    }

    if (balance === undefined && !balanceLoading) {
      errorMessage = "Unable to load balance information";
    }
  }

  if (hasUnprintableSelection) {
    if (isSingleUnprintableSelection || allSelectionUnprintable) {
      errorMessage = getNoLabelErrorMessage(fulfillmentGroups);
    } else {
      const unprintableGroups = fulfillmentGroups.filter(
        (fg) => !fg.shipment?.label?.url,
      );
      const unprintableMessage = getNoLabelErrorMessage(unprintableGroups);
      warningMessage = unprintableMessage.replace(
        "No label",
        "Some orders have no labels",
      );
    }
  }

  if (
    selectedPreset &&
    selectedPreset?.carrier &&
    selectedPreset?.service &&
    selectedRate &&
    (selectedRate?.carrier !== selectedPreset?.carrier ||
      selectedRate?.service !== selectedPreset?.service)
  ) {
    warningMessage =
      "The shipping service specified in the preset is not available";
  }

  const someVoided = fulfillmentGroups.some(
    (fg) => fg.status === FulfillmentGroupStatus.Voided,
  );

  const allVoided = fulfillmentGroups.every(
    (fg) => fg.status === FulfillmentGroupStatus.Voided,
  );

  const oneVoided = fulfillmentGroups.length === 1 && someVoided;

  if (oneVoided) {
    errorMessage = "This label has been voided";
  } else if (allVoided) {
    errorMessage = "All selected labels have been voided";
  } else if (someVoided) {
    errorMessage = "Some selected labels have been voided";
  }

  if (
    fulfillmentGroups.some(
      (fg) => fg.status === FulfillmentGroupStatus.OpenNotReceived,
    )
  ) {
    if (fulfillmentGroups.length === 1) {
      errorMessage =
        "This shipment contains items that have not been received. You must first mark them as received in the marketplace before fulfilling this shipment.";
    } else {
      errorMessage =
        "These shipments contain items that have not been received. You must first mark them as received in the marketplace before fulfilling these shipments.";
    }
  }

  if (ordersOnHold.length > 0) {
    errorMessage =
      ordersOnHold.length === 1
        ? "This shipment is on hold. Release the hold to fulfill order."
        : "These shipments are on hold. Release the holds to fulfill orders.";
  }

  if (
    fulfillmentGroups.some((fg) =>
      fg.providers?.some((provider) =>
        ProviderCannotUpdateTrackingInfo.includes(provider),
      ),
    )
  ) {
    if (fulfillmentGroups.length === 1) {
      voidConfirmationMessage =
        "Updating tracking info in the marketplace is not supported for this shipment. This could affect seller performance metrics for that platform.";
    } else {
      voidConfirmationMessage =
        "Updating tracking info in the marketplace is not supported for some of the selected shipments. This could affect seller performance metrics for that platform.";
    }
  }

  // Seller shipping tiktok orders can only be voided once. Tiktok shipping tiktok orders
  // can't be voided at all (Void button won't be shown - handled elsewhere)
  if (fulfillmentGroups.some((fg) => fg.providers?.includes(Provider.TIKTOK))) {
    voidConfirmationMessage =
      "Tracking info for TikTok orders shipped by seller can only be updated once. Are you sure you'd like to proceed?";
  }

  if (
    fulfillmentGroups.some(
      (group) =>
        group.shipmentPaymentOption?.type ===
        ShipmentPaymentOptionType.THIRD_PARTY,
    ) &&
    isCreateAction
  ) {
    if (fulfillmentGroups.length === 1) {
      if (
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        isThirdPartyOnlyPayingVat(fulfillmentGroups[0].shipmentPaymentOption)
      ) {
        warningMessage =
          "This shipment will be billed to a third party account. The third party will only pay for VAT.";
      } else {
        warningMessage =
          "This shipment will be billed to a third party account.";
      }
    } else if (
      fulfillmentGroups.every(
        (group) =>
          group.shipmentPaymentOption?.type ===
          ShipmentPaymentOptionType.THIRD_PARTY,
      )
    ) {
      const onlyPayingVat = fulfillmentGroups.every((group) =>
        isThirdPartyOnlyPayingVat(group.shipmentPaymentOption),
      );
      if (onlyPayingVat) {
        warningMessage =
          "All of the selected shipments will be billed to a third party account. The third party will only pay for VAT.";
      } else {
        warningMessage =
          "All of the selected shipments will be billed to a third party account.";
      }
    } else {
      warningMessage =
        "Some of the selected shipments will be billed to a third party account.";
    }
  }

  return { errorMessage, warningMessage, voidConfirmationMessage };
}

export function calculateNeedsFunds(
  balance: string | undefined,
  totalChargedToRedoBalance: number | null,
  canPurchaseEveryGroup: boolean,
  minimumBalance: number,
): boolean {
  // Not open => we don't need funds because we're not trying to buy labels
  if (!canPurchaseEveryGroup) {
    return false;
  }

  // If we don't have a rate to buy, we don't need funds
  if (totalChargedToRedoBalance == null) {
    return false;
  }

  // Nothing will be charged to the Redo balance (e.g. every label is on the
  // merchant's own carrier account / BYOA), so the purchase can't move their
  // balance past the floor. Never block on funds in this case, even if their
  // balance is already below the floor.
  if (totalChargedToRedoBalance <= 0) {
    return false;
  }

  // If the new balance will be less than the minimum, we need funds
  return Number(balance) - totalChargedToRedoBalance < Number(minimumBalance);
}

export function parcelListEqual(
  a: Parcel[] | null,
  b: Parcel[] | null,
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return arrayEqual(isParcelEqual)(a, b);
}

export function getCheapestMatchingRate({
  carrier,
  service,
  group,
}: {
  carrier: string;
  service: string;
  group: FulfillmentGroupData;
}): OutboundRate | undefined {
  const rates = group.availableShipmentRates?.rates.filter(
    (rate) => rate.carrier === carrier && rate.service === service,
  );
  if (rates?.length) {
    return rates.reduce((prev, curr) =>
      Number(prev.rate) < Number(curr.rate) ? curr : prev,
    );
  }
  return undefined;
}

export function getTrackingCodesWithLinks(
  group: FulfillmentGroupData,
): { code: string; link: string }[] {
  const trackingCodesWithLinks: { code: string; link: string }[] = [];

  if (group.shipment?.tracker?.trackingCode) {
    const link = getTrackingLink(
      group.shipment.tracker.carrier,
      group.shipment.tracker.trackingCode,
      group.shipment.tracker.publicUrl,
      group.shipment.boughtRate?.service,
    );
    trackingCodesWithLinks.push({
      code: group.shipment.tracker.trackingCode,
      link,
    });
  }

  for (const additionalShipment of group.additionalShipments ?? []) {
    if (additionalShipment.tracker?.trackingCode) {
      const link = getTrackingLink(
        additionalShipment.tracker.carrier,
        additionalShipment.tracker.trackingCode,
        additionalShipment.tracker.publicUrl,
        additionalShipment.boughtRate?.service,
      );
      trackingCodesWithLinks.push({
        code: additionalShipment.tracker.trackingCode,
        link,
      });
    }
  }

  if (group.shipmentDetails?.freightShipment) {
    trackingCodesWithLinks.push({
      code: group.shipmentDetails.freightShipment.tracker.trackingCode,
      link: group.shipmentDetails.freightShipment.tracker.publicUrl,
    });
  }

  // Remove duplicates based on tracking code
  const uniqueCodes = new Map<string, string>();
  for (const { code, link } of trackingCodesWithLinks) {
    if (!uniqueCodes.has(code)) {
      uniqueCodes.set(code, link);
    }
  }

  return Array.from(uniqueCodes.entries()).map(([code, link]) => ({
    code,
    link,
  }));
}

export function formatTrackingCodesWithLinks(
  trackingCodesWithLinks: { code: string; link: string }[],
): { code: string; link: string }[] | null {
  if (trackingCodesWithLinks.length === 0) {
    return null;
  }

  if (trackingCodesWithLinks.length <= 3) {
    return trackingCodesWithLinks;
  }

  return trackingCodesWithLinks.slice(0, 3);
}

export const getFirstOrderNavigationUrl = (
  row: Pick<FulfillmentGroupData, "_id" | "orders">,
  // Flag-aware orders-outbound-labels base (`/stores/X/shipping/outbound-labels`
  // or `/stores/X/order-management/outbound-labels`). Pass
  // `routes.ordersOutboundLabelsHref` from the caller so the link lands at
  // the active route tree directly instead of going through a
  // `NavOrgRedirect` hop — that hop briefly puts the URL outside any
  // primary nav tab's pathRoot and unmounts the side nav.
  ordersOutboundLabelsHref: string,
  // OMS-3209: when supplied, the order detail page surfaces the source
  // fulfillment view in its breadcrumb trail. Pass the URL slug
  // (`useParams().viewName`) of whichever fulfillment view the click
  // originated from.
  sourceView?: string,
): string | null => {
  const firstOrder = row.orders.find((order) => order.name);

  if (!firstOrder) {
    return null;
  }

  const orderId = firstOrder.redoOrderId
    ? { redoOrderId: firstOrder.redoOrderId }
    : { id: firstOrder.id };

  const sourceViewParam = sourceView
    ? `&sourceView=${encodeURIComponent(sourceView)}`
    : "";

  return "redoOrderId" in orderId
    ? `${ordersOutboundLabelsHref}/orders/${orderId.redoOrderId}?outboundOrderTab=${row._id}&isRedoOrder=true${sourceViewParam}`
    : `${ordersOutboundLabelsHref}/orders/${btoa(orderId.id)}?outboundOrderTab=${row._id}&isRedoOrder=false${sourceViewParam}`;
};
